import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery, {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import BioentitySearchInput, {BioentitySearchInputProps} from 'inventory/BioentitySearchInput';
import FieldError from 'ui-library/FieldError';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {ConnectionSchemaEquipmentQuery} from './__generated/ConnectionSchemaEquipmentQuery';
import {
  EquipmentSearchInputQuery,
  EquipmentSearchInputQueryVariables,
} from './__generated/EquipmentSearchInputQuery';
import {EQUIPMENT_SCHEMA_INTERFACE_ID} from './EquipmentConstants';

type EquipmentSearchInputProps = Omit<
  BioentitySearchInputProps & {connectionSchemaId?: string | null},
  'bioentityTypes' | 'placeholderText' | 'tagSchemaOrInterfaceId'
>;

export const CONNECTION_SCHEMA_EQUIPMENT_QUERY = gql`
  query ConnectionSchemaEquipmentQuery($connectionSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: INSTRUMENTS) {
    connectionSchema(id: $connectionSchemaId) {
      ... on ConnectionSchemaNode {
        id
        equipmentIds
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function EquipmentSearchInput(props: EquipmentSearchInputProps): JSX.Element {
  const connectionsResponse = useSkippableBenchlingQuery<ConnectionSchemaEquipmentQuery>(
    CONNECTION_SCHEMA_EQUIPMENT_QUERY,
    {
      variables: {
        connectionSchemaId: props.connectionSchemaId,
      },
      skip: !props.connectionSchemaId,
    }
  );

  if (props.connectionSchemaId) {
    if (!connectionsResponse || connectionsResponse.type !== QueryResultType.SUCCESS) {
      return <CenteredHorizontalThrobber />;
    }

    if (isInaccessible(connectionsResponse.data.connectionSchema)) {
      return <FieldError message="Connection schema of run is inaccessible" />;
    }
    return (
      <BioentitySearchInput
        bioentityTypes={[ModelTypeConstants.BASIC_FOLDER_ITEM]}
        placeholderText="Search for an instrument"
        tagSchemaOrInterfaceId={EQUIPMENT_SCHEMA_INTERFACE_ID}
        validIds={connectionsResponse.data.connectionSchema?.equipmentIds}
        {...props}
      />
    );
  }
  return (
    <BioentitySearchInput
      bioentityTypes={[ModelTypeConstants.BASIC_FOLDER_ITEM]}
      placeholderText="Search for an instrument"
      tagSchemaOrInterfaceId={EQUIPMENT_SCHEMA_INTERFACE_ID}
      {...props}
    />
  );
}

export const EQUIPMENT_SEARCH_INPUT_QUERY: DocumentNode = gql`
  query EquipmentSearchInputQuery($id: StrictString!) @skipAudit @engComponent(name: INSTRUMENTS) {
    equipment(id: $id) {
      ... on EquipmentNode {
        ...BioentitySummaryGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('BioentitySummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function QueryingEquipmentSearchInput(
  props: Omit<EquipmentSearchInputProps, 'selectedItems'> & {equipmentId: string | null}
): JSX.Element {
  return (
    <BenchlingQuery<EquipmentSearchInputQuery, EquipmentSearchInputQueryVariables>
      query={EQUIPMENT_SEARCH_INPUT_QUERY}
      variables={{id: props.equipmentId!}}
      renderLoading={() => <CenteredHorizontalThrobber />}
      skip={props.equipmentId == null}
    >
      {(data) => (
        <EquipmentSearchInput
          {...props}
          selectedItems={props.equipmentId ? accessibleObjs([data?.equipment]) : []}
        />
      )}
    </BenchlingQuery>
  );
}
